// src/product/uri.ts
import { z } from "zod";
var AudioNoteReferenceSchema = z.object({
  type: z.literal("audioNote"),
  id: z.string().min(1)
}).strict();
var StagedActionReferenceSchema = z.object({
  type: z.literal("stagedAction"),
  id: z.string().min(1)
}).strict();
var PRODUCT_REFERENCES = {
  audioNote: { route: "audio-notes", schema: AudioNoteReferenceSchema },
  stagedAction: { route: "staged-actions", schema: StagedActionReferenceSchema }
};
function highlightProductUri(reference) {
  const { route, schema } = PRODUCT_REFERENCES[reference.type];
  const parsed = schema.parse(reference);
  return `highlight://${route}/${encodeURIComponent(parsed.id)}`;
}
function parseHighlightProductUri(value) {
  let url;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.protocol !== "highlight:" && url.protocol !== "highlight-dev:" || url.username || url.password || url.port || url.search || url.hash) {
    return null;
  }
  const pathSegments = url.pathname.slice(1).split("/");
  const [route, ...idSegments] = url.hostname ? [url.hostname, ...pathSegments] : pathSegments;
  if (idSegments.length !== 1 || !idSegments[0]) {
    return null;
  }
  const matched = Object.entries(PRODUCT_REFERENCES).find(([, spec]) => spec.route === route);
  if (!matched) {
    return null;
  }
  const [type, { schema }] = matched;
  try {
    const parsed = schema.safeParse({ type, id: decodeURIComponent(idSegments[0]) });
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
function audioNoteUri(reference) {
  return highlightProductUri(reference);
}
function parseAudioNoteUri(value) {
  const reference = parseHighlightProductUri(value);
  return reference?.type === "audioNote" ? reference : null;
}
function stagedActionUri(reference) {
  return highlightProductUri(reference);
}
function parseStagedActionUri(value) {
  const reference = parseHighlightProductUri(value);
  return reference?.type === "stagedAction" ? reference : null;
}

// src/product/attachments.ts
var ATTACHMENT_PREFIX = "attachment:";
var ATTACHMENT_REFERENCE = /\[[\s\S]*?\]\(attachment:(\d+)\)/g;
function isAttachmentUrl(url) {
  return typeof url === "string" && url.startsWith(ATTACHMENT_PREFIX);
}
function parseAttachmentIndex(url) {
  if (!isAttachmentUrl(url)) return null;
  const index = Number.parseInt(url.slice(ATTACHMENT_PREFIX.length), 10);
  return Number.isFinite(index) && index >= 0 ? index : null;
}
function missingAttachmentReferences(markdown, count) {
  const referenced = new Set(Array.from(markdown.matchAll(ATTACHMENT_REFERENCE), (match) => Number(match[1])));
  const missing = [];
  for (let index = 0; index < count; index++) {
    if (!referenced.has(index)) missing.push(`![Image ${index + 1}](${ATTACHMENT_PREFIX}${index})`);
  }
  return missing;
}

// src/product/attachment-limits.ts
var MAX_UPLOAD_FILE_BYTES = 50 * 1024 * 1024;
var MAX_UPLOAD_FILE_LABEL = "50 MB";
var MAX_INLINE_TEXT_BYTES = 19e5;
var MAX_INLINE_TEXT_LABEL = "1.9 MB";
export {
  AudioNoteReferenceSchema,
  MAX_INLINE_TEXT_BYTES,
  MAX_INLINE_TEXT_LABEL,
  MAX_UPLOAD_FILE_BYTES,
  MAX_UPLOAD_FILE_LABEL,
  StagedActionReferenceSchema,
  audioNoteUri,
  highlightProductUri,
  isAttachmentUrl,
  missingAttachmentReferences,
  parseAttachmentIndex,
  parseAudioNoteUri,
  parseHighlightProductUri,
  parseStagedActionUri,
  stagedActionUri
};
//# sourceMappingURL=index.js.map